import * as THREE from 'three';

// Clip the real body and number planes in the die's local space. Interpolating
// UVs keeps each half of a sliced number attached to its original face.
const clipGeometry = (geometry, matrix, side, rim) => {
  const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  source.applyMatrix4(matrix);
  const positions = source.getAttribute('position');
  const normals = source.getAttribute('normal');
  const uvs = source.getAttribute('uv');
  const values = { position: [], normal: [], uv: [] };
  const remember = (vertex) => {
    if (rim && Math.abs(vertex.position.x) < 0.000001) {
      const { y, z } = vertex.position;
      rim.set(`${y.toFixed(6)}:${z.toFixed(6)}`, new THREE.Vector3(0, y, z));
    }
  };
  for (let index = 0; index < positions.count; index += 3) {
    const triangle = [0, 1, 2].map((offset) => ({
      position: new THREE.Vector3().fromBufferAttribute(
        positions,
        index + offset
      ),
      normal: normals
        ? new THREE.Vector3().fromBufferAttribute(normals, index + offset)
        : new THREE.Vector3(0, 0, 1),
      uv: uvs
        ? new THREE.Vector2().fromBufferAttribute(uvs, index + offset)
        : new THREE.Vector2(),
    }));
    const polygon = [];
    triangle.forEach((start, edge) => {
      const end = triangle[(edge + 1) % 3];
      const inside = start.position.x * side >= 0;
      const endInside = end.position.x * side >= 0;
      if (inside) polygon.push(start);
      remember(start);
      if (inside !== endInside) {
        const amount = start.position.x / (start.position.x - end.position.x);
        const crossing = {
          position: start.position.clone().lerp(end.position, amount).setX(0),
          normal: start.normal.clone().lerp(end.normal, amount).normalize(),
          uv: start.uv.clone().lerp(end.uv, amount),
        };
        polygon.push(crossing);
        remember(crossing);
      }
    });
    for (let corner = 1; corner < polygon.length - 1; corner += 1)
      [polygon[0], polygon[corner], polygon[corner + 1]].forEach((vertex) => {
        values.position.push(...vertex.position.toArray());
        values.normal.push(...vertex.normal.toArray());
        values.uv.push(...vertex.uv.toArray());
      });
  }
  source.dispose();
  const clipped = new THREE.BufferGeometry();
  clipped.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(values.position, 3)
  );
  clipped.setAttribute(
    'normal',
    new THREE.Float32BufferAttribute(values.normal, 3)
  );
  clipped.setAttribute('uv', new THREE.Float32BufferAttribute(values.uv, 2));
  return clipped;
};

const closeCut = (rim, side) => {
  const points = [...rim.values()].sort(
    (a, b) => Math.atan2(a.z, a.y) - Math.atan2(b.z, b.y)
  );
  const positions = [];
  points.forEach((point, index) => {
    const next = points[(index + 1) % points.length];
    const edge = side < 0 ? [point, next] : [next, point];
    positions.push(0, 0, 0, ...edge[0].toArray(), ...edge[1].toArray());
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.computeVertexNormals();
  return geometry;
};

export const createDefeatDieScene = (source, color) => {
  const group = new THREE.Group();
  group.name = 'defeat-die';
  group.add(source);
  const split = new THREE.Group();
  split.name = 'defeat-die-halves';
  group.add(split);
  const decay = { value: 0 };
  const time = { value: 0 };
  const materials = [];
  const addGlitch = (material) => {
    material.transparent = true;
    material.onBeforeCompile = (shader) => {
      shader.uniforms.defeatDecay = decay;
      shader.uniforms.defeatTime = time;
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          '#include <common>\nvarying vec3 defeatPosition;'
        )
        .replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\ndefeatPosition = transformed;'
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          '#include <common>\nuniform float defeatDecay;\nuniform float defeatTime;\nvarying vec3 defeatPosition;'
        )
        .replace(
          '#include <clipping_planes_fragment>',
          `#include <clipping_planes_fragment>
          float band = floor(defeatPosition.y * 32.0);
          float signal = fract(sin(band * 17.731 + floor(defeatTime * 10.0) * 0.37) * 43758.5453);
          if (defeatDecay > 0.0 && signal < defeatDecay) discard;
        `
        );
    };
    material.customProgramCacheKey = () => 'defeat-die-signal-loss';
    materials.push({
      material,
      opacity: material.opacity,
      emissive: material.emissiveIntensity,
    });
    return material;
  };
  const halves = [-1, 1].map((side) => {
    const half = new THREE.Group();
    half.name = side < 0 ? 'defeat-die-left' : 'defeat-die-right';
    const rim = new Map();
    source.children.forEach((child) => {
      if (!child.isMesh) return;
      child.updateMatrix();
      const isBody = !child.material.map;
      const geometry = clipGeometry(
        child.geometry,
        child.matrix,
        side,
        isBody ? rim : null
      );
      if (!geometry.getAttribute('position').count) {
        geometry.dispose();
        return;
      }
      const mesh = new THREE.Mesh(geometry, addGlitch(child.material.clone()));
      mesh.userData.slicedBody = isBody;
      half.add(mesh);
      if (isBody)
        half.add(
          new THREE.LineSegments(
            new THREE.EdgesGeometry(geometry, 25),
            addGlitch(new THREE.LineBasicMaterial({ color, opacity: 0.7 }))
          )
        );
    });
    const cut = new THREE.Mesh(
      closeCut(rim, side),
      addGlitch(
        new THREE.MeshStandardMaterial({
          color: '#160d24',
          emissive: color,
          emissiveIntensity: 0.55,
          metalness: 0.3,
          roughness: 0.6,
          side: THREE.DoubleSide,
        })
      )
    );
    cut.name = 'defeat-die-cut-face';
    half.add(cut);
    half.add(
      new THREE.LineSegments(
        new THREE.EdgesGeometry(cut.geometry, 25),
        addGlitch(new THREE.LineBasicMaterial({ color, opacity: 0.9 }))
      )
    );
    split.add(half);
    return { half, side, cut };
  });
  const pixels = Array.from({ length: 26 }, (_, index) => {
    const pixel = new THREE.Mesh(
      new THREE.BoxGeometry(0.02 + (index % 4) * 0.012, 0.01, 0.012),
      new THREE.MeshBasicMaterial({
        color: index % 3 ? color : '#ff5b91',
        transparent: true,
        depthWrite: false,
      })
    );
    pixel.visible = false;
    group.add(pixel);
    return pixel;
  });
  return {
    group,
    update(elapsed, reduceMotion = false) {
      const cutAt = 1.05;
      const separation = THREE.MathUtils.smoothstep(elapsed, cutAt, 2.0);
      const fade = THREE.MathUtils.smoothstep(elapsed, 2.0, 3.15);
      source.visible = reduceMotion || elapsed < cutAt;
      split.visible = !reduceMotion && elapsed >= cutAt && elapsed < 3.2;
      group.rotation.set(
        -0.18 + Math.min(elapsed, cutAt) * 0.22,
        0.3 + Math.min(elapsed, cutAt) * 0.35,
        -0.35
      );
      const tick = Math.floor(elapsed * 12);
      const jitter = elapsed > 1.8 ? Math.sin(tick * 2.7) * 0.055 * fade : 0;
      halves.forEach(({ half, side }) => {
        half.position.set(
          side * separation * 0.43 + side * jitter,
          -side * separation * 0.08 - fade * 0.15,
          0
        );
        half.rotation.set(
          side * separation * 0.15,
          side * separation * 0.38,
          -side * separation * 0.18
        );
      });
      decay.value = fade;
      time.value = elapsed;
      materials.forEach(({ material, opacity, emissive }) => {
        material.opacity = opacity * (1 - fade);
        if (emissive !== undefined)
          material.emissiveIntensity = emissive * (1 - fade);
      });
      pixels.forEach((pixel, index) => {
        const progress = THREE.MathUtils.clamp(
          (elapsed - 1.55 - (index % 5) * 0.09) / 1.4,
          0,
          1
        );
        pixel.visible = !reduceMotion && progress > 0 && progress < 1;
        const side = index % 2 ? 1 : -1;
        pixel.position.set(
          side * (0.15 + progress * (0.45 + (index % 4) * 0.13)),
          ((index % 9) - 4) * 0.07 - progress * 0.1,
          0.15 + (index % 3) * 0.1
        );
        pixel.scale.x = 1 + Math.abs(Math.sin(tick + index)) * progress * 5;
        pixel.material.opacity = (1 - progress) * 0.65;
      });
    },
  };
};
